import { Injectable } from '@angular/core';

import { Project } from './../../model/project/project';
import { ProjectMember } from '../../model/project/project-classes';
import { ResearchContract } from 'app/fs/res/common/model/finance/research-contract-classes';
import { RestrictedValidity } from 'app/fs/res/common/model/restricted_validity';

@Injectable()
export class ProjectPeriodService {
    private lastProject: Project;

    constructor() { }

    setLastProject(project: Project) {
        this.lastProject = JSON.parse(JSON.stringify(project));
    }

    clearLastProjectPeriod() {
        this.lastProject = null;
    }

    getProjectMembersWithInvalidPeriod(
        project: Project,
        projectMembers: ProjectMember[]
    ): {projectMember: ProjectMember; manualAdj: boolean, startdateIsInvalid: boolean, enddateIsInvalid: boolean}[] {
        return projectMembers.map(pM => ({
            projectMember: pM,
            manualAdj: this.requireManualAdjustment(pM, project.startdate, project.enddate),
            startdateIsInvalid: this.hasInvalidStartdate(pM, project.startdate, project.enddate),
            enddateIsInvalid: this.hasInvalidEnddate(pM, project.startdate, project.enddate)
        })).filter(pM => pM.startdateIsInvalid || pM.enddateIsInvalid);
    }

    getResearchContractsWithInvalidPeriod(
        project: Project,
        researchContracts: ResearchContract[]
    ): {researchContract: ResearchContract; manualAdj: boolean, startdateIsInvalid: boolean, enddateIsInvalid: boolean}[] {
        return researchContracts.map(rC => ({
            researchContract: rC,
            manualAdj: this.requireManualAdjustment(rC, project.startdate, project.enddate),
            startdateIsInvalid: this.hasInvalidStartdate(rC, project.startdate, project.enddate),
            enddateIsInvalid: this.hasInvalidEnddate(rC, project.startdate, project.enddate)
        })).filter(rC => rC.startdateIsInvalid || rC.enddateIsInvalid);
    }

    updatePeriod(entity: RestrictedValidity, projectStartdate: Date | string, projectEnddate: Date | string) {
        if(this.lastProject && this.projectPeriodChanged(projectStartdate, projectEnddate)) {
            const lastEntityValue = this.getLastEntityValue(entity);

            if(entity.validFrom &&
                (this.datesAreEqual(this.lastProject.startdate, lastEntityValue.validFrom) || entity.validFrom < projectStartdate)
            ) {
                entity.validFrom = new Date(projectStartdate);
            }

            if(entity.validTo &&
                (this.datesAreEqual(this.lastProject.enddate, lastEntityValue.validTo) || entity.validTo > projectEnddate)
            ) {
                entity.validTo = new Date(projectEnddate);
            }
        }
    }

    hasInvalidPeriod(entity: RestrictedValidity, projectStartdate: Date | string, projectEnddate: Date | string) {
        return this.hasInvalidStartdate(entity, projectStartdate, projectEnddate) ||
            this.hasInvalidEnddate(entity, projectStartdate, projectEnddate);
    }

    hasInvalidStartdate(entity: RestrictedValidity, projectStartdate: Date | string, projectEnddate: Date | string) {
        if(!entity.validFrom || !projectStartdate || !projectEnddate) {
            return false;
        } else if(this.lastProject && this.projectPeriodChanged(projectStartdate, projectEnddate)) {
            const lastEntityValue = this.getLastEntityValue(entity);

            return (this.datesAreEqual(this.lastProject.startdate, lastEntityValue.validFrom) &&
                !this.datesAreEqual(entity.validFrom, projectStartdate)) || entity.validFrom < projectStartdate ||
                this.requireManualAdjustment(entity, projectStartdate, projectEnddate);
        } else {
            return false;
        }
    }

    hasInvalidEnddate(entity: RestrictedValidity, projectStartdate: Date | string, projectEnddate: Date | string) {
        if(!entity.validTo || !projectStartdate || !projectEnddate) {
            return false;
        } else if(this.lastProject && this.projectPeriodChanged(projectStartdate, projectEnddate)) {
            const lastEntityValue = this.getLastEntityValue(entity);

            return (this.datesAreEqual(this.lastProject.enddate, lastEntityValue.validTo) &&
                !this.datesAreEqual(entity.validTo, projectEnddate)) || entity.validTo > projectEnddate ||
                this.requireManualAdjustment(entity, projectStartdate, projectEnddate);
        } else {
            return false;
        }
    }

    requireManualAdjustment(entity: RestrictedValidity, projectStartdate: Date | string, projectEnddate: Date | string) {
        return (entity.validFrom < projectStartdate && entity.validTo < projectStartdate) ||
            (entity.validFrom > projectEnddate && entity.validTo >projectEnddate);
    }

    private getLastEntityValue(entity: RestrictedValidity) {
        return (entity.hasOwnProperty('personId') && !entity.hasOwnProperty('budgetSource')) ?
            this.lastProject.members.find(e => e.id === entity.id) ?? entity :
            this.lastProject.fundingPrograms.find(e => e.id === entity.id) ?? entity;
    }

    private projectPeriodChanged(projectStartdate: Date | string, projectEnddate: Date | string) {
        return !this.datesAreEqual(this.lastProject.startdate, projectStartdate) ||
            !this.datesAreEqual(this.lastProject.enddate, projectEnddate);
    }

    private datesAreEqual(date1: Date | string, date2: Date | string) {
        return new Date(date1).toDateString() === new Date(date2).toDateString();
    }
}
